<template>
    <div v-if="profile != null">
        <m-make-payment-app-bar
            :value="organization_label"
            :banner_branding="banner_branding"
        />

        <div style="padding-left: 30px; padding-right: 30px;">
            <div
                v-if="form.is_archived"
                style="text-align: center; margin-top: 100px;"
            >
                <h1 class="text-h5 mb-5">
                    <strong>This form is no longer available.</strong>
                </h1>
            </div>

            <div v-else>

                <div
                    v-if="show_thank_you"
                    style="text-align: center; margin-top: 30px;"
                >
                    <div style="font-size: 32px;">
                        <h1
                            class="text-h5"
                            style="margin-bottom: 30px;"
                        >
                            <strong>{{ form.after_submit_title }}</strong>
                        </h1>
                    </div>

                    <div>
                        <m-showdown :value="form.md_after_submit_instructions" />
                    </div>
                </div>

                <div v-else>
                    <h1
                        v-if="form.title != null && form.title.length > 0"
                        style="margin-top: 30px; margin-bottom: 20px"
                        class="text-h5"
                    >
                        <strong>
                            {{ form.title }}
                        </strong>
                    </h1>

                    <p style="margin-bottom: 30px">
                        <m-showdown :value="form.md_instructions" />
                    </p>

                    <rc-base-input
                        v-if="!has_payment"
                        label="First Name"
                        type="text"
                        v-model="first_name"
                        autocomplete="given-name"
                        required
                    />

                    <rc-base-input
                        v-if="!has_payment"
                        label="Last Name"
                        type="text"
                        v-model="last_name"
                        autocomplete="family-name"
                        required
                    />

                    <rc-base-input
                        v-if="!has_payment"
                        label="Phone Number"
                        type="tel"
                        v-model="phone_number"
                        required
                        @validated="phone_input_valid = $event"
                    />

                    <m-custom-form-field
                        v-for="(item, index) in fields"
                        class="custom-form-field"
                        :key="'custom_form_field_' + index"
                        :field="item"
                        :profile="profile"
                        @input="onChangeCustomField(item, $event)"
                        @input-payment="onChangePayment"
                    />

                    <div v-if="robust_age_gate">
                        <p>
                            You must be {{ default_age_gate_age }} or older to receive our text messages. Please enter
                            your birthday in MM/DD/YYYY format so we can verify your age
                        </p>

                        <rc-base-input
                            label="Birthday"
                            type="birthday"
                            v-model="birthday"
                            required
                            @validated="birthday_input_valid = $event"
                        />
                    </div>

                    <m-legal-checkbox-agreement
                        v-if="!has_payment"
                        ref="agreement"
                        :organization_id="organization_id"
                        type="default-opt-in-form-agreement"
                        v-model="agreement_cb"
                        style="margin-bottom: 15px"
                        required
                        :override_patterns="profile.effective_consent_agreement_patterns"
                    />

                    <rc-alert
                        v-model="show_alert"
                        color="primary"
                        type="error"
                        style="margin-top: 15px"
                        dismissible
                        :restore-focus-to="$refs.submitButton"
                    >
                        {{ alert_text }}
                    </rc-alert>

                    <div style="text-align: right; padding-top: 15px; margin-bottom: 30px;">
                        <v-btn
                            ref="submitButton"
                            color="secondary"
                            @click="onClickSubmit"
                        >
                            Submit
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>

        <confirm-payment-details-dialog
            v-if="is_providing_payment_info"
            v-model="show_payment_info_confirmation_dialog"
            :pending_payment_info="payment_info"
            @confirmed="onConfirmPaymentInfo"
            @cancelled="onEditPaymentInfo"
        />
    </div>
</template>

<script>
import RcAlert from '@/components/shared/alerts/rc-alert.vue';
import RcBaseInput from '@/components/shared/inputs/rc-base-input.vue';
import ConfirmPaymentDetailsDialog from '@/views/payments/public/confirm-payment-details-dialog.vue';

export default {
    components: {
        RcAlert,
        RcBaseInput,
        ConfirmPaymentDetailsDialog
    },

    data() {
        return {
            banner_branding: null,
            organization_account: null,
            form: null,
            fields: null,
            agreement_cb: false,

            first_name: null,
            last_name: null,
            phone_number: null,
            phone_input_valid: false,
            birthday: null,
            birthday_input_valid: false,

            custom_field_values: {},

            show_alert: false,
            alert_text: null,

            show_thank_you: false,

            profile: null,

            payment_info: null,
            show_payment_info_confirmation_dialog: false,
            payment_info_confirmed: false,
        }
    },

    props: {
        in_test_mode: {
            type: Boolean,
            default: function () { return false; }
        },

        preview: {
            type: Boolean,
            default: function () { return false; }
        },
    },

    mounted() {
        this.update();
    },

    methods: {
        update() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'GET: /api/v1/forms/profile/:form_id',

                params: {
                    form_id: this.$route.params.form_id,
                },

                on_success: (data) => {
                    this.form = data.form;
                    this.fields = data.fields;
                    this.banner_branding = data.banner_branding;
                    this.organization_account = data.organization_account;
                    this.profile = data;

                    if (!this.preview && this.form?.title) {
                        const formTitle = this.form.title.length > 30
                            ? `${this.form.title.substring(0, 30)}...`
                            : this.form.title;
                        document.title = `${formTitle} | ${this.organization_label}`;
                    }
                },

                on_error: 'Unable to load form',
            });
        },

        toggleShowAfterSubmit() {
            this.show_thank_you = !this.show_thank_you;
        },

        isBlank(value) {
            if (value == null) return true;
            if (value.trim().length == 0) return true;
            return false;
        },

        onChangeCustomField(item, new_value) {
            this.custom_field_values[item.form_field_id] = new_value;
        },

        onChangePayment(value) {
            this.payment_info = value;
            this.payment_info_confirmed = false;
        },

        onClickSubmit() {
            if (!this.has_payment && this.agreement_cb == false) {
                this.show_alert = true;
                this.alert_text = 'You must check the box granting us permission to text you.';
                return;
            }

            if (!this.has_payment && this.isBlank(this.first_name)) {
                this.show_alert = true;
                this.alert_text = 'A first name is required';
                return false;
            }

            if (!this.has_payment && this.isBlank(this.last_name)) {
                this.show_alert = true;
                this.alert_text = 'A last name is required';
                return false;
            }

            if (!this.has_payment && (this.isBlank(this.phone_number) || !this.phone_input_valid)) {
                this.show_alert = true;
                this.alert_text = 'A valid phone number is required';
                return false;
            }

            if (this.robust_age_gate && (this.isBlank(this.birthday) || !this.birthday_input_valid)) {
                this.show_alert = true;
                this.alert_text = 'A valid birth date (MM/DD/YYYY) is required';
                return false;
            }

            if (this.payment_info != null && this.payment_info.error != null) {
                this.show_alert = true;
                this.alert_text = this.payment_info.error;
                return;
            }

            this.show_alert = false;

            if (this.is_providing_payment_info && !this.payment_info_confirmed) {
                this.show_payment_info_confirmation_dialog = true;
                return;
            }

            let post_data = {
                form_id: this.$route.params.form_id,

                first_name: this.first_name,
                last_name: this.last_name,
                phone_number: this.phone_number,
                birthday: this.birthday,

                agreement_specimen: this.agreement_cb == true ? this.$refs['agreement'].getSpecimen() : null,

                in_test_mode: this.in_test_mode,
                payment_info: this.payment_info,

                ...this.custom_field_values,
            };

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/forms/submit',

                params: post_data,

                on_success: (data) => {
                    if (data.valid == false) {
                        this.alert_text = data.message;
                        this.show_alert = true;
                        return;
                    }

                    if (this.form.after_submit_redirect_to_custom_url && !this.in_test_mode) {
                        document.location = this.form.after_submit_redirect_url;
                        return;
                    }

                    this.show_thank_you = true;
                },

                on_error: 'Unable to submit form',
            });
        },

        onConfirmPaymentInfo() {
            this.show_payment_info_confirmation_dialog = false;
            this.payment_info_confirmed = true;
            this.onClickSubmit();
        },

        onEditPaymentInfo() {
            this.show_payment_info_confirmation_dialog = false;
            this.payment_info_confirmed = false;
        },
    },

    computed: {
        is_providing_payment_info() {
            return this.payment_info != null;
        },

        robust_age_gate() {
            if (this.has_payment)
                return false;

            if (this.organization_account == null)
                return false;

            return this.organization_account.industry === 'wine';
        },

        organization_label() {
            if (this.organization_account == null) return '';
            return this.organization_account.label;
        },

        organization_id() {
            if (this.organization_account == null) return '';
            return this.organization_account.organization_id;
        },

        has_payment() {
            if (this.fields == null) return false;

            for (let i = 0; i < this.fields.length; i++) {
                if (this.fields[i].type === 'payment')
                    return true;
            }

            return false;
        },

        default_age_gate_age() {
            // Use form's age gate if set, otherwise default to 21
            if (this.form != null && this.form.age_gate_min_age_in_years != null) {
                return this.form.age_gate_min_age_in_years;
            }
            return 21;
        },
    },
}
</script>

<style lang="scss" scoped>
.custom-form-field {
    margin-top: 1rem;

    &:not(:last-of-type) {
        margin-bottom: 1rem;
    }
}

/*
    When used as text/bg with white text, our primary color is not AA compliant on a white bg.
    This should be moved to global.css eventually ...
    But that will necessitate more work than just these styles for consistency sake.
    Only applying to public forms for now.
*/
div.v-application .primary--text {
    color: var(--v-primary-darken1) !important;
    caret-color: var(--v-primary-darken1) !important;
}

div.v-application .error--text {
    color: var(--v-error-darken1) !important;
    caret-color: var(--v-error-darken1) !important;
}

div.v-alert.primary {
    background-color: var(--v-primary-darken1) !important;
    border-color: var(--v-primary-darken1) !important;
}

/* default light grey background is not sufficient on its own for AA accessibility */
:deep(.v-list-item--highlighted) {
    outline: 2px solid #000;
}
</style>